/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import isEqual from 'lodash.isequal';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceTypes} from '@ion/core/src/services/Connect';
import {
  DeviceType
} from '@ion/core/src/types/entities';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {VisionDeviceType} from '@ion/dls/src/editors/DeviceEditorUtils';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptions,
  getOptionSelected
} from '@ion/features/src/helpers';

// defines the device types chooser prop definitions ...
export type DeviceTypeChooserProps = {
  onChange?: (deviceType: DeviceType) => void;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  defaultValue?: string;
  devicetypes?: DeviceType[];
  isHardwarePart?: boolean;
  removeVisionCamera?: boolean;
}

// defines the styles used by device types chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
export const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : dlsZIndex('deviceTypeChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  formControl : {
    borderBottom           : ({hasError}: {hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: {hasError: boolean;}) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : 'none !important'
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * Provides a utility which filters a list of device types for the
 * currently selected id.
 */
const getSelectedValue = (
  devicetypes: DeviceType[],
  selected: string
): DeviceType => {
  return devicetypes?.find(({name}: DeviceType) => name === selected);
};

/**
 * The DeviceTypesChooser provides a list of device types for the user's default account
 * for display within a dropdown list and allows the user to search and select an device types
 * from the list which will be returned to the client as the selected device types.
 *
 * ```typescript
 *
 *     import {DeviceTypesChooser} from '@ion/features/src/devicetypes';
 *
 *     // read-only mode ...
 *     <DeviceTypesChooser readOnly
 *       />
 *
 *     // with default value and required check ...
 *     <DeviceTypesChooser required
 *       />
 *
 *     // with callback subscription ...
 *     <DeviceTypesChooser onChange={setDeviceTypes}
 *       />
 *
 * ```
 *
 *
 * @devicetypes      Allows for providing a default Device types
 * @onChange         Callback prop which will be invoked with the seleted device types.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @defaultValue     Default Value of the field
 */
export const DeviceTypeChooser = ({
  onChange,
  readOnly,
  devicetypes,
  required,
  classes,
  defaultValue,
  hasError = false,
  isHardwarePart = false,
  removeVisionCamera = false
}: DeviceTypeChooserProps) => {
  const _classes                      = useStyles({hasError});
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>(null);
  const [open, setOpen]               = useState(false);
  const [isLoading, setIsLoading]     = useState<boolean>(false);
  const [selected, setSelected]       = useState<string>(devicetypes?.name || null);
  const [options, setOptions]         = useState<Options[]>([]);
  const hasMounted                    = useHasMounted();

  useEffect(() => {
    // manages loading device types based on current search term ...
    const loadDeviceTypes = async function() {
      let deviceTypes: any;
      setIsLoading(true);
      try {
        const results = await getDeviceTypes();
        deviceTypes = results?.items?.sort((a, b) => a.name > b.name ? 1 : -1);
      } catch (err) {
        error('DeviceTypes: Error occurred retrieving device types');
      } finally {
        if (hasMounted !== false) {
          if (removeVisionCamera) {
            deviceTypes = deviceTypes?.filter(({name}) => !isEqual(name.toUpperCase(), VisionDeviceType));
          }
          setDeviceTypes(deviceTypes);
          setOptions(getOptions(deviceTypes));
          setIsLoading(false);
          if (defaultValue) {
            onChange(getSelectedValue(deviceTypes, defaultValue));
          } else if (selected) {
            onChange?.(undefined);
          }
        }
      }
    };

    loadDeviceTypes();
  }, []);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    if (e) {
      e.persist();
    }
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly}
          defaultValue={defaultValue || ''}
          autoComplete={false}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={translate('common:features.devicetypes.clearText')}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          onChange={onChange ? function(event, value: Options) {
            const val = value?.label;
            setSelected(val);
            onChange(value ? getSelectedValue(deviceTypes, val) : null);
          } : null}
          options={options}
          loading={isLoading}
          selectOnFocus={false}
          clearOnEscape
          loadingText={translate('common:states.loading')}
          noOptionsText={translate('common:features.devicetypes.noOptionsText')}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                error={hasError}
                helperText={hasError && required ? translate('common:editors.shared.required') : ''}
                required={required}
                fieldLabel={isHardwarePart ? '' : translate('common:features.devicetypes.label')}
                placeholder={translate('common:features.devicetypes.placeholder')}
                name={translate('common:features.devicetypes.name')}
                InputProps={{
                  ...params.InputProps
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default DeviceTypeChooser;
